import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onMounted, ref, watch } from 'vue';
import store from '@/store';
import Billing from '@/components/Billing/Billing.vue';
import Integrations from '@/components/Integrations/Integrations.vue';
import AccountComponent from '@/components/Account/Account.vue';
import PodBilling from '@/components/Billing/PodBilling.vue';
import Team from '@/components/Team/Team.vue';
import { hasIntegrationAccess } from '@/api/user';
import { StoreType } from '@/typings/user';


export default /*#__PURE__*/_defineComponent({
  __name: 'Account',
  emits: ['user-updated'],
  setup(__props, { emit }) {



const currentPage: Ref<'account' | 'billing' | 'integrations' | 'podBilling' | 'team'> = ref('account');
const loading = ref(true);

const user = computed(() => store.state.user);
const isImpersonate = computed(() => user.value?.ImpersonatedFromChildUser);
const isCollectiveRetailUser = computed(
    () => user.value?.AccessRoles?.Serialized?.split(',')?.includes('collective-retail') === true
);

const manageIntegrations = ref(true);

watch(currentPage, () => {
    if (!displayBillingPanel.value && currentPage.value === 'billing') {
        currentPage.value = 'account';
    }

    // If we don't have a customer, don't let access the Pod Billing section
    if (!hasPODBilling.value && currentPage.value === 'podBilling') {
        currentPage.value = 'account';
    }

    // If don't have access to integrations, don't let access integrations tab
    if (!hasManageIntegrations.value && currentPage.value === 'integrations') {
        currentPage.value = 'account';
    }

    // If don't have access to team, don't let access team tab
    if (!hasTeam.value && currentPage.value === 'team') {
        currentPage.value = 'account';
    }

    // Set the corresponding query parameter depending on which section we currently are
    const url = new URL(location.href);
    url.searchParams.set('section', currentPage.value);
    window.history.replaceState({}, document.title, `${url.pathname}?${url.searchParams.toString()}`);
});

const displayBillingPanel = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    // We only show the billing panel if the store has a Stripe subscription or if it’s a Shopify store on the new plan.
    return hasBilling.value || hasShopifyBilling.value;
});

// Shopify stores using the new plan
const hasShopifyBilling = computed(() => {
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }
    //sh new plan
    return user.value?.Stores.some((s) => s.Type === StoreType.Shopify && !s.HasBilling);
});

// Stores with stripe subscription (Etsy, Woo, or Shopify using the old plan, etc)
const hasBilling = computed(() => {
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }

    return user.value?.Stores.some((s) => s.HasBilling);
});

const hasTeam = computed(() => !isCollectiveRetailUser.value);

const hasPODBilling = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }

    return !hasBilling.value && user.value?.Stores.some((s) => s.HasPODBilling);
});

const hasAffiliate = computed(() => {
    if (user.value?.AccessRoles == null) return false;
    return user.value?.AccessRoles?.Serialized.includes('affiliate');
});

const hasManageIntegrations = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    if (isImpersonate.value) {
        return manageIntegrations.value;
    }

    if (hasAffiliate.value) {
        return user.value?.Stores?.some((s) => !s.Disabled) ?? false;
    }

    return true;
});

onMounted(async () => {
    try {
        const url = new URL(location.href);
        const section = url.searchParams.get('section');
        if (section) {
            switch (section.toLowerCase()) {
                case 'billing':
                    currentPage.value = 'billing';
                    break;
                case 'integrations':
                    currentPage.value = 'integrations';
                    break;
                case 'podBilling':
                    currentPage.value = 'podBilling';
                    break;
                case 'team':
                    currentPage.value = 'team';
                    break;
                default:
                    currentPage.value = 'account';
            }
        }

        manageIntegrations.value = await hasIntegrationAccess();
    } finally {
        loading.value = false;
    }
});

const onUserUpdated = () => {
    emit('user-updated');
};

return { __sfc: true,emit, currentPage, loading, user, isImpersonate, isCollectiveRetailUser, manageIntegrations, displayBillingPanel, hasShopifyBilling, hasBilling, hasTeam, hasPODBilling, hasAffiliate, hasManageIntegrations, onUserUpdated, Billing, Integrations, AccountComponent, PodBilling, Team }
}

})